import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  User as UserIcon,
  type LucideIcon,
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { useCurrentScope } from '../../hooks/useMobilisations';
import { buildNavSections, type NavLeaf } from './navConfig';
import { NavBadge } from './NavBadge';

/**
 * Persistent left sidebar for the authenticated app. Collapses to an
 * icon rail; choice is stored per-browser in localStorage so it sticks
 * across sessions. Hidden below the `lg` breakpoint — the MobileNavDrawer
 * handles smaller screens.
 */

const STORAGE_KEY = 'hcc:nav:collapsed';

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(v: boolean) {
  try {
    window.localStorage.setItem(STORAGE_KEY, v ? '1' : '0');
  } catch {
    // ignore (private mode, quota, etc.)
  }
}

export function Sidebar() {
  const { data: user, isLoading } = useCurrentUser();
  const scope = useCurrentScope();
  const [collapsed, setCollapsed] = useState(false);

  // Hydrate from localStorage on mount to avoid an SSR-style flash.
  useEffect(() => {
    setCollapsed(readCollapsed());
  }, []);

  function toggle() {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(next);
      return next;
    });
  }

  if (isLoading || !user) return null;

  const sections = buildNavSections(user, {
    dayToDay: scope?.kind === 'onboarding',
  });
  // Inductees have no primary nav, so an empty rail is just blank space and
  // visually confusing. Drop it entirely and let the content centre; the brand
  // and "My record" live in the TopBar in that case.
  if (sections.length === 0) return null;

  return (
    <aside
      aria-label="Primary navigation"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={[
        'hidden h-full shrink-0 flex-col border-r border-line/10 bg-surface lg:flex print:hidden',
        'transition-[width] duration-200 ease-in-out',
        collapsed ? 'w-16' : 'w-64',
      ].join(' ')}
    >
      <BrandHeader collapsed={collapsed} />

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {sections.map((entry, i) => {
          if (entry.kind === 'leaf') {
            return (
              <SidebarItem
                key={entry.to}
                to={entry.to}
                icon={entry.icon}
                label={entry.label}
                badge={entry.badge}
                exact={entry.exact}
                collapsed={collapsed}
              />
            );
          }
          return (
            <Section
              key={i}
              title={entry.label}
              collapsed={collapsed}
              addDivider={i > 0}
            >
              {entry.items.map((leaf) => (
                <SidebarItem
                  key={leaf.to}
                  to={leaf.to}
                  icon={leaf.icon}
                  label={leaf.label}
                  badge={leaf.badge}
                  exact={leaf.exact}
                  collapsed={collapsed}
                />
              ))}
            </Section>
          );
        })}
      </nav>

      <div className="mt-auto border-t border-divider/10 p-2">
        {user.person_id ? (
          <SidebarItem
            to="/me"
            icon={UserIcon}
            label="My record"
            collapsed={collapsed}
          />
        ) : null}
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={[
            'mt-1 flex w-full items-center gap-3 rounded-token-sm px-3 py-2 text-sm font-medium text-fg-muted',
            'min-h-touch transition-colors hover:bg-surface-2 hover:text-fg',
            collapsed ? 'justify-center' : '',
          ].join(' ')}
        >
          {collapsed ? (
            <ChevronsRight className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <>
              <ChevronsLeft className="h-4 w-4 shrink-0" aria-hidden />
              <SidebarLabel collapsed={collapsed}>Collapse</SidebarLabel>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}

function BrandHeader({ collapsed }: { collapsed: boolean }) {
  return (
    <Link
      to="/"
      className={[
        'flex h-14 shrink-0 items-center gap-3 border-b border-line/10 px-4 transition-colors hover:bg-surface-2',
        collapsed ? 'justify-center px-2' : '',
      ].join(' ')}
    >
      <span
        aria-hidden
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-token-sm text-fg-on-accent shadow-token-accent ring-1 ring-accent/30"
        style={{
          backgroundImage:
            'linear-gradient(135deg, rgb(var(--accent)) 0%, #ec4899 100%)',
        }}
      >
        <ClipboardList className="h-4 w-4" />
      </span>
      {!collapsed ? (
        <span className="truncate text-sm font-semibold tracking-tight text-fg">
          Onboarding
        </span>
      ) : null}
    </Link>
  );
}

interface SectionProps {
  title: string;
  collapsed: boolean;
  addDivider: boolean;
  children: React.ReactNode;
}

function Section({ title, collapsed, addDivider, children }: SectionProps) {
  return (
    <div
      className={[
        addDivider ? 'mt-3 pt-3' : '',
        addDivider && collapsed ? 'border-t border-divider/10' : '',
      ].join(' ')}
    >
      {!collapsed ? (
        <div className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-dim">
          {title}
        </div>
      ) : null}
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

interface SidebarItemProps {
  to: string;
  icon: LucideIcon;
  label: string;
  collapsed: boolean;
  badge?: NavLeaf['badge'];
  exact?: boolean;
}

function SidebarItem({ to, icon: Icon, label, collapsed, badge, exact }: SidebarItemProps) {
  return (
    <NavLink
      to={to}
      end={to === '/' || !!exact}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        [
          'group relative flex items-center gap-3 rounded-token-sm px-3 py-2 text-sm font-medium',
          'min-h-touch transition-colors',
          collapsed ? 'justify-center' : '',
          isActive
            ? 'bg-accent/10 text-accent'
            : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {isActive ? (
            <span
              aria-hidden
              className="absolute -left-2 top-1.5 bottom-1.5 w-0.5 rounded-full bg-accent"
            />
          ) : null}
          <Icon
            className={[
              'h-4 w-4 shrink-0 transition-colors',
              isActive ? 'text-accent' : 'text-fg-dim group-hover:text-fg',
            ].join(' ')}
            aria-hidden
          />
          <SidebarLabel collapsed={collapsed}>{label}</SidebarLabel>
          {badge ? <NavBadge kind={badge} collapsed={collapsed} /> : null}
        </>
      )}
    </NavLink>
  );
}

function SidebarLabel({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: React.ReactNode;
}) {
  return (
    <span
      className={[
        'min-w-0 truncate transition-opacity duration-150',
        collapsed ? 'pointer-events-none w-0 opacity-0' : 'opacity-100',
      ].join(' ')}
    >
      {children}
    </span>
  );
}
